import { useRef, useState, useLayoutEffect } from 'react';
import { getEventAestheticOrNone, EVENT_AESTHETIC_BLEED } from './registry';
import { eventBannerThumb } from '../../utils/media';

// Web port of app/components/eventAesthetics/AestheticEventCard.jsx. Same layout
// + size as the default list card — only colours/photo tint change and a
// decorative SVG frame is overlaid (bleeding into the reserved margin). Pass
// `aesthetic` to force a descriptor (the picker uses this for previews);
// otherwise it resolves from event.aesthetic. `previewMode` drops the bleed
// margin + click target.

const CARD_RADIUS = 12;
// Banner height is responsive (16:9 of the card's own width, capped) rather than
// a flat pixel value — on web the card can stretch far wider than on the app's
// fixed phone width, and a fixed height there crops the photo down to a sliver.
const IMAGE_ASPECT = 16 / 9;
const IMAGE_MAX_H = 240;
const imageHeightFor = (width) => (width ? Math.min(IMAGE_MAX_H, Math.round(width / IMAGE_ASPECT)) : IMAGE_MAX_H);

const C = { textPrimary: '#ffffff', textSecondary: '#8e8e93', accent: '#5b7fff', warning: '#f5a623' };

// Local category palette (mirrors client/src/pages/Events.jsx). Only used for
// the plain "none" preview accent/badge — themed cards use the aesthetic colour.
const categoryColor = {
  Event: '#5b7fff', Meet: '#ff7c3f', 'Car Meet': '#ff7c3f', Cruise: '#4caf7d',
  'Track Day': '#e84545', 'Roll Racing': '#f5a623', 'Show & Shine': '#7c6ff7',
  'Cars & Coffee': '#ecdfc4', Other: '#8892b0',
};
const normalizeCategory = (c) => (c === 'Car Meet' ? 'Meet' : (c || ''));

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }) : '');
const fmtTime = (d) => (d ? new Date(d).toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' }) : '');

// Measures the rendered element so the frame can size to it (RN onLayout equiv).
function useSize() {
  const ref = useRef(null);
  const [size, setSize] = useState(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => {
      const r = el.getBoundingClientRect();
      setSize((prev) => (prev && prev.width === r.width && prev.height === r.height) ? prev : { width: r.width, height: r.height });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, size];
}

function PeopleIcon({ color }) {
  return (
    <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

export default function AestheticEventCard({ event, onClick, previewMode = false, aesthetic: aestheticProp }) {
  const aesthetic = aestheticProp || getEventAestheticOrNone(event?.aesthetic);
  const [ref, size] = useSize();

  const isNone = !aesthetic || aesthetic.id === 'none';
  const cat = normalizeCategory(event?.category || (Array.isArray(event?.tags) ? event.tags[0] : ''));
  const catColor = categoryColor[cat] || '#8892b0';

  const accent = isNone ? catColor : aesthetic.accent;
  const badgeColor = isNone ? catColor : (aesthetic.badgeColor || aesthetic.accent);
  const badgeTextColor = isNone ? catColor : (aesthetic.badgeTextColor || badgeColor);
  const surface = isNone ? undefined : aesthetic.surface;
  const metaColor = isNone ? C.textSecondary : (aesthetic.metaColor || C.textSecondary);
  const Frame = isNone ? null : aesthetic.Frame;
  const Layout = isNone ? null : aesthetic.Layout;
  const titleStyle = isNone ? null : aesthetic.titleStyle;
  const metaStyle = isNone ? null : aesthetic.metaStyle;
  const bleed = (aesthetic && aesthetic.bleed) || EVENT_AESTHETIC_BLEED;

  const clickable = !previewMode && typeof onClick === 'function';
  const outerBase = { position: 'relative', cursor: clickable ? 'pointer' : 'default' };

  // A custom-layout aesthetic renders its OWN full card (different arrangement).
  if (Layout) {
    return (
      <div style={outerBase} onClick={clickable ? onClick : undefined}>
        <Layout event={event} />
      </div>
    );
  }

  return (
    <div
      style={{ ...outerBase, margin: previewMode ? 0 : `${bleed}px 0` }}
      onClick={clickable ? onClick : undefined}
    >
      <div
        ref={ref}
        style={{ backgroundColor: surface || '#1c1c1e', borderRadius: CARD_RADIUS, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
      >
        {event?.bannerImageUrl ? (
          <div style={{ position: 'relative', width: '100%', aspectRatio: IMAGE_ASPECT, maxHeight: IMAGE_MAX_H, overflow: 'hidden' }}>
            <img src={eventBannerThumb(event)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          </div>
        ) : null}
        <div style={{ height: 2, backgroundColor: accent }} />
        <div style={{ padding: 14 }}>
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span style={{ alignSelf: 'flex-start', padding: '3px 8px', borderWidth: 1, borderStyle: 'solid', borderColor: badgeColor + '66', backgroundColor: badgeColor + '24', borderRadius: 999, fontSize: 10, fontWeight: 700, letterSpacing: 0.5, color: badgeTextColor }}>
              {cat || 'Event'}
            </span>
            {event && event.isPublic === false ? (
              <span style={{ backgroundColor: 'rgba(236,238,242,0.92)', borderRadius: 999, padding: '2px 8px', fontSize: 10, fontWeight: 700, letterSpacing: 0.5, color: '#14181d' }}>PRIVATE</span>
            ) : null}
          </div>
          <div style={{ fontSize: 15, fontWeight: 700, color: C.textPrimary, marginTop: 6, marginBottom: 4, ...(titleStyle || {}), display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2, overflow: 'hidden' }}>
            {event?.title}
          </div>
          {event?.eventDate ? (
            <div style={{ fontSize: 12, color: metaColor, marginTop: 2, ...(metaStyle || {}) }}>{fmtDate(event.eventDate)} · {fmtTime(event.eventDate)}</div>
          ) : null}
          {event?.address ? (
            <div style={{ fontSize: 12, color: metaColor, marginTop: 2, ...(metaStyle || {}), whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{event.address}</div>
          ) : null}
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: 10 }}>
            {(event?.attendeeCount > 0 || event?.interestedCount > 0) ? (
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                <PeopleIcon color={metaColor} />
                <span style={{ fontSize: 12, color: metaColor, ...(metaStyle || {}) }}>
                  {(event.attendeeCount ?? 0)} going{event.interestedCount > 0 ? ` · ${event.interestedCount} interested` : ''}
                </span>
              </div>
            ) : <div />}
            {event?.myStatus ? (
              isNone ? (
                <span style={{ padding: '3px 8px', borderWidth: 1, borderStyle: 'solid', borderRadius: 999, fontSize: 10, fontWeight: 700, color: C.textSecondary, ...(event.myStatus === 'attending' ? { borderColor: C.accent + '50', backgroundColor: C.accent + '18' } : { borderColor: C.warning + '50', backgroundColor: C.warning + '18' }) }}>
                  {event.myStatus === 'attending' ? 'Going' : 'Interested'}
                </span>
              ) : (
                <span style={{ padding: '3px 8px', borderWidth: 1, borderStyle: 'solid', borderRadius: 999, fontSize: 10, fontWeight: 700, borderColor: accent + '70', backgroundColor: accent + '24', color: accent }}>
                  {event.myStatus === 'attending' ? 'Going' : 'Interested'}
                </span>
              )
            ) : null}
          </div>
        </div>
      </div>
      {size && Frame ? (
        <Frame
          w={size.width}
          h={(aesthetic.frameImageOnly && event?.bannerImageUrl) ? imageHeightFor(size.width) : size.height}
          radius={CARD_RADIUS}
          accent={accent}
        />
      ) : null}
    </div>
  );
}
